/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemFrame } from "../../../shared/contracts";
import { EventProxy } from "../../core/event-proxy";
import type { IHittable } from "../../core/ihittable";
import type { IMovable } from "../../core/imovable";
import type { IPlayable } from "../../core/iplayable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import { degToRad } from "../../math/float";
import type { Vertex2D } from "../../math/vertex2d";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";
import type { FlipperData } from "./flipper-data";
import { FlipperHit } from "./flipper-hit";
import type { FlipperMover } from "./flipper-mover";
import { FlipperState } from "./flipper-state";

/**
 * A flipper.
 */
export class Flipper extends Item<FlipperData> implements IPlayable, IMovable, IHittable {
  private readonly state: FlipperState;
  private hit?: FlipperHit;

  public constructor(data: FlipperData) {
    super(data);
    this.state = new FlipperState(
      this.getName(),
      degToRad(this.data.startAngle),
      this.data.isVisible,
    );
  }

  public isCollidable(): boolean {
    return true;
  }

  public getMover(): FlipperMover {
    return this.hit!.getMoverObject();
  }

  public getState(): FlipperState {
    return this.state;
  }

  public setupPlayer(player: Player, table: Table): void {
    this.events = new EventProxy(this, "flipper", player.getEventSink());
    this.hit = FlipperHit.getInstance(
      this.data,
      this.state,
      this.events,
      player.getPhysics(),
      table,
    );
  }

  public getHitShapes(): HitObject[] {
    return [this.hit!];
  }

  public getFlipperData(): FlipperData {
    return this.data;
  }

  /** Which button moves this flipper. */
  public getSide(): "left" | "right" {
    return this.data.side;
  }

  /** Button down: move to the end angle. */
  public rotateToEnd(): void {
    const mover = this.getMover();
    mover.enableRotateEvent = 1;
    mover.setSolenoidState(true);
  }

  /** Button up: return to the rest angle. */
  public rotateToStart(): void {
    const mover = this.getMover();
    mover.enableRotateEvent = -1;
    mover.setSolenoidState(false);
  }

  public toFrame(): ItemFrame {
    return this.state.toFrame();
  }
}

export interface FlipperConfig {
  center: Vertex2D;
  baseRadius: number;
  endRadius: number;
  flipperRadius: number;
  angleStart: number;
  angleEnd: number;
  zLow: number;
  zHigh: number;
}
